/**
 * Cap for the reference-paragraph context carried on an insert card. Long
 * enough to recognize the paragraph, short enough that the inserted content
 * stays the focus of the preview.
 */
const ANCHOR_PREVIEW_MAX_CHARS = 180;

/**
 * The slice of the reference paragraph to show beside an insertion so the
 * reader can see where the new content lands. The insertion abuts the
 * paragraph's tail when it goes after it and its head when it goes before it,
 * so a paragraph too long to show whole keeps the abutting end and the
 * ellipsis marks the side that was cut. A short paragraph is shown whole.
 *
 * Returns undefined when there is nothing to show — a blank anchor paragraph —
 * which the cards render as no context at all.
 */
export function buildInsertAnchorPreview(options: {
  position: 'after' | 'before';
  text: string;
}): string | undefined {
  const { position, text } = options;
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (!collapsed) return undefined;
  if (collapsed.length <= ANCHOR_PREVIEW_MAX_CHARS) return collapsed;

  if (position === 'after') {
    return `…${keepWholeWordsFromTail(collapsed, collapsed.length - ANCHOR_PREVIEW_MAX_CHARS)}`;
  }
  return `${keepWholeWordsFromHead(collapsed, ANCHOR_PREVIEW_MAX_CHARS)}…`;
}

/**
 * The text from `cutIndex` on, dropping the word the cut landed inside. A cut
 * that already sits on a word boundary keeps everything after it.
 */
function keepWholeWordsFromTail(text: string, cutIndex: number): string {
  if (text[cutIndex - 1] === ' ') return text.slice(cutIndex);
  const spaceIndex = text.indexOf(' ', cutIndex);
  if (spaceIndex === -1) return text.slice(cutIndex);
  return text.slice(spaceIndex + 1);
}

/**
 * The text up to `cutIndex`, dropping the word the cut landed inside. A cut
 * that already sits on a word boundary keeps everything before it.
 */
function keepWholeWordsFromHead(text: string, cutIndex: number): string {
  if (text[cutIndex] === ' ') return text.slice(0, cutIndex);
  const spaceIndex = text.lastIndexOf(' ', cutIndex);
  if (spaceIndex === -1) return text.slice(0, cutIndex);
  return text.slice(0, spaceIndex);
}
